
export default function Refunds(){
  return (
    <div className="container py-12 prose max-w-4xl">
      <h1>Refund & Cancellation Policy</h1>
      <p className="text-sm text-gray-600">Effective Date: January 1, 2025 | Last Updated: January 1, 2025</p>

      <h2>1. Overview</h2>
      <p>
        This Refund and Cancellation Policy explains the terms for cancelling services and requesting
        refunds from AdKarrot LLC. Different refund terms apply to different service types due to the
        nature of digital marketing work, which involves immediate time investment, research, and
        strategic planning.
      </p>
      <p className="font-bold">
        IMPORTANT: This policy is part of our <a href="/legal/terms">Terms of Service</a>. By purchasing
        services, you agree to these refund and cancellation terms.
      </p>

      <h2>2. No Guaranteed Results</h2>
      <div className="p-4 bg-yellow-50 border border-yellow-200 rounded">
        <p className="font-bold">Digital marketing results vary and cannot be guaranteed.</p>
        <p>
          Results depend on numerous factors outside our control, including search engine algorithm
          changes, competitor actions, market conditions, your website quality, industry dynamics,
          and economic conditions.
        </p>
        <p className="font-bold mt-2">
          We DO NOT guarantee specific rankings, traffic, conversions, ROI, or revenue targets.
        </p>
        <p>
          We guarantee professional effort, industry best practices, and transparent reporting.
          <strong> Refunds are not based on results.</strong>
        </p>
      </div>

      <h2>3. Strategy Call (One-Time Service)</h2>
      <p><strong>Service Details:</strong> 60-90 minute consultation providing strategic marketing guidance</p>

      <h3>3.1 Cancellation Before Session</h3>
      <ul>
        <li><strong>48+ hours before:</strong> FULL REFUND (minus Stripe processing fees: 2.9% + $0.30)</li>
        <li><strong>24-48 hours before:</strong> 50% REFUND</li>
        <li><strong>Less than 24 hours:</strong> NO REFUND (session forfeited)</li>
      </ul>

      <h3>3.2 Rescheduling</h3>
      <ul>
        <li>You may reschedule <strong>once</strong> without penalty with 48 hours notice</li>
        <li>Second reschedule or no-show: Session forfeited, no refund</li>
      </ul>

      <h3>3.3 No-Show Policy</h3>
      <ul>
        <li>If you do not attend the scheduled session: NO REFUND</li>
        <li>We will wait 15 minutes past the scheduled time</li>
      </ul>

      <h3>3.4 After Session Completion</h3>
      <ul>
        <li>NO REFUNDS once the session has occurred</li>
        <li>You receive all promised deliverables (recording, notes, action plan)</li>
      </ul>

      <p><strong>Refund Processing Time:</strong> 5-7 business days to original payment method</p>

      <h2>4. SEO/PPC Audit (One-Time Service)</h2>
      <p><strong>Service Details:</strong> Comprehensive audit with analysis and actionable recommendations</p>
      <p><strong>Payment Structure:</strong> 50% deposit to begin work, 50% upon delivery</p>

      <h3>4.1 Cancellation Before Work Begins</h3>
      <ul>
        <li>Cancel before audit work starts: FULL DEPOSIT REFUND minus 10% processing fee</li>
        <li>Cancel after audit begins: NO REFUND (see partial completion below)</li>
      </ul>

      <h3>4.2 Partial Completion</h3>
      <p>If you cancel mid-project:</p>
      <ul>
        <li>We invoice for hours worked at our standard hourly rate</li>
        <li>Remaining deposit balance is refunded</li>
        <li>You receive all work completed to that point</li>
      </ul>

      <h3>4.3 After Delivery</h3>
      <ul>
        <li>NO REFUNDS after final audit report is delivered</li>
        <li>We provide one round of clarification questions at no additional charge</li>
      </ul>

      <h3>4.4 Dissatisfaction with Deliverables</h3>
      <p>If you are unsatisfied with the audit quality:</p>
      <ul>
        <li>Notify us within <strong>5 business days</strong> of delivery</li>
        <li>Provide specific concerns in writing</li>
        <li>We will review and address legitimate quality concerns</li>
        <li>We may provide additional analysis or clarification</li>
        <li>Refunds at our discretion if deliverables don't meet the stated scope</li>
      </ul>

      <h3>4.5 Turnaround Time</h3>
      <ul>
        <li>Standard turnaround: 10-14 business days from deposit payment</li>
        <li>If delivery exceeds 30 days without your approval: You may request full deposit refund</li>
      </ul>

      <p><strong>Refund Processing Time:</strong> 7-10 business days to original payment method</p>

      <h2>5. Monthly Growth Retainer (Recurring Subscription)</h2>
      <p><strong>Service Details:</strong> Ongoing monthly marketing services with defined deliverables</p>
      <p><strong>Billing:</strong> Automatic monthly charge on subscription start date</p>

      <h3>5.1 Billing Cycle</h3>
      <ul>
        <li><strong>First payment:</strong> Due on signup date</li>
        <li><strong>Renewal:</strong> Automatic on the same date each month</li>
        <li><strong>Failed payment:</strong> Services suspended after 5 business days</li>
      </ul>

      <h3>5.2 Cancellation Policy</h3>
      <p className="font-bold">Cancellation requires 30 days written notice via email to billing@adkarrot.com</p>
      <ul>
        <li>Services continue through the end of the paid period</li>
        <li><strong>NO REFUNDS for partial months</strong></li>
        <li>Example: Cancel on May 15th → Services end June 30th (if paid through June)</li>
      </ul>

      <h3>5.3 Immediate Cancellation</h3>
      <p>You may request immediate cancellation:</p>
      <ul>
        <li>NO REFUND for the current month (services already provided or reserved)</li>
        <li>Access to deliverables and accounts continues through end of paid month</li>
        <li>All recurring charges stop immediately</li>
      </ul>

      <h3>5.4 First Month Satisfaction Guarantee</h3>
      <p>If you are unsatisfied within the first 30 days:</p>
      <ul>
        <li>Request a refund with specific reasons for dissatisfaction</li>
        <li>Allow us 5 business days to address your concerns</li>
        <li>If we cannot resolve the issues: Full refund of first month only</li>
        <li>Applies to <strong>first-time clients only</strong></li>
      </ul>

      <h3>5.5 Pause Subscription</h3>
      <ul>
        <li>You may pause your retainer for up to 3 months</li>
        <li>Email request 30 days in advance to billing@adkarrot.com</li>
        <li>No charges during pause period</li>
        <li>All marketing work stops during pause (campaigns paused, no deliverables)</li>
      </ul>

      <h3>5.6 Payment Failures</h3>
      <ul>
        <li><strong>Failed payment:</strong> Email notification + 5-day grace period</li>
        <li><strong>After 5 days:</strong> Services suspended, campaigns paused</li>
        <li><strong>After 15 days:</strong> Accounts closed, $50 reactivation fee required</li>
        <li>You remain responsible for unpaid amounts plus late fees</li>
      </ul>

      <p><strong>Refund Processing Time:</strong> 10-14 business days to original payment method</p>

      <h2>6. Service Modifications</h2>

      <h3>6.1 Price Changes</h3>
      <ul>
        <li><strong>One-time services:</strong> Price locked at booking</li>
        <li><strong>Monthly retainers:</strong> 30 days written notice for price increases</li>
        <li>You may cancel within 30 days of price increase notice without penalty</li>
      </ul>

      <h3>6.2 Service Interruptions</h3>
      <p>If we cannot provide services due to circumstances within our control:</p>
      <ul>
        <li>We will notify you immediately</li>
        <li>Pro-rated credit for services not delivered</li>
        <li>Option to reschedule or receive refund for undelivered portion</li>
      </ul>

      <h3>6.3 Platform Changes</h3>
      <ul>
        <li>If Google, Facebook, or other platforms change policies affecting your services:</li>
        <li>We will notify you of impacts</li>
        <li>We will adapt strategy within existing scope</li>
        <li>NO REFUNDS for external platform changes outside our control</li>
      </ul>

      <h2>7. Account Ownership & Transition</h2>

      <h3>7.1 Your Accounts</h3>
      <p>You own all advertising and marketing accounts:</p>
      <ul>
        <li>Google Ads, Google Analytics, Search Console</li>
        <li>Facebook Business Manager, Ad Accounts</li>
        <li>LinkedIn Campaign Manager</li>
        <li>All data and content in your accounts</li>
      </ul>
      <p>Upon cancellation, you retain full access to your accounts.</p>

      <h3>7.2 Our Deliverables</h3>
      <p>You retain copies of:</p>
      <ul>
        <li>Reports, analyses, and strategic recommendations</li>
        <li>Custom content created for you</li>
        <li>Campaign configurations (remain in your accounts)</li>
      </ul>
      <p>We retain ownership of:</p>
      <ul>
        <li>Proprietary methodologies and processes</li>
        <li>Internal tools and templates</li>
      </ul>

      <h3>7.3 Transition Assistance</h3>
      <p>For retainer clients upon cancellation:</p>
      <ul>
        <li>30 days of transition assistance included</li>
        <li>Final reports and documentation provided</li>
        <li>Coordination with new agency if requested</li>
      </ul>
      <p>For one-time services:</p>
      <ul>
        <li>Deliverables provided as contracted</li>
        <li>No additional transition assistance included</li>
      </ul>

      <h2>8. Disputes and Chargebacks</h2>

      <h3>8.1 Contact Us First</h3>
      <p className="font-bold">If you are unsatisfied with services, please contact us BEFORE filing a chargeback:</p>
      <ol>
        <li>Email <a href="mailto:support@adkarrot.com">support@adkarrot.com</a> or call 319-343-6094</li>
        <li>Provide specific concerns and desired resolution</li>
        <li>We will respond within 2 business days</li>
        <li>We will work in good faith to resolve the issue</li>
      </ol>

      <h3>8.2 Chargeback Policy</h3>
      <div className="p-4 bg-red-50 border border-red-200 rounded">
        <p className="font-bold">DO NOT FILE A CHARGEBACK WITHOUT CONTACTING US FIRST.</p>
        <p>If you dispute a charge with your bank/credit card without contacting us, we reserve the right to:</p>
        <ul>
          <li>Terminate services immediately</li>
          <li>Deny future services</li>
          <li>Pursue collection for legitimate charges plus fees</li>
          <li>Add a $50 processing fee if chargeback is reversed in our favor</li>
        </ul>
      </div>

      <h3>8.3 Legitimate Disputes</h3>
      <p>We will work with you to resolve legitimate disputes and may issue refunds for:</p>
      <ul>
        <li>Services not delivered as promised in our agreement</li>
        <li>Significant quality issues not addressed after notification</li>
        <li>Misrepresentation of services or deliverables</li>
      </ul>

      <h3>8.4 Fraudulent Chargebacks</h3>
      <p>
        Filing a fraudulent chargeback for services properly rendered may result in legal action to
        recover funds, collection agency involvement, and responsibility for attorney fees and court
        costs if we prevail.
      </p>

      <h2>9. Refund Processing</h2>

      <h3>9.1 Processing Time</h3>
      <ul>
        <li><strong>Strategy Calls:</strong> 5-7 business days</li>
        <li><strong>Audits:</strong> 7-10 business days</li>
        <li><strong>Retainers:</strong> 10-14 business days</li>
      </ul>

      <h3>9.2 Refund Method</h3>
      <ul>
        <li>Refunds issued to original payment method via Stripe</li>
        <li>We cannot issue refunds to a different card or account</li>
        <li>Stripe processing fees (2.9% + $0.30) are non-refundable</li>
      </ul>

      <h3>9.3 Notification</h3>
      <ul>
        <li>You will receive email confirmation when refund is processed</li>
        <li>Contact your bank if refund not received within stated timeframe + 5 business days</li>
      </ul>

      <h2>10. Contact for Cancellations & Refunds</h2>
      <p>To cancel services or request a refund:</p>

      <p>
        <strong>Email (Preferred):</strong> <a href="mailto:billing@adkarrot.com">billing@adkarrot.com</a><br/>
        <strong>Subject:</strong> "Cancellation Request" or "Refund Request"
      </p>

      <p><strong>Include:</strong></p>
      <ul>
        <li>Your name and business name</li>
        <li>Service(s) to cancel or reason for refund request</li>
        <li>Invoice number or payment date</li>
        <li>Preferred contact method</li>
      </ul>

      <p>
        <strong>Phone:</strong> 319-343-6094 (business hours: Monday-Friday, 9 AM - 5 PM Central)<br/>
        <strong>Mail:</strong> AdKarrot LLC, Attn: Billing Department, 309 Court Avenue Suite 855, Des Moines, IA 50309
      </p>

      <p>
        We will respond within <strong>2 business days</strong> and process approved refunds within the
        timeframes stated above.
      </p>

      <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded">
        <p className="text-sm font-semibold">Quick Reference:</p>
        <ul className="text-sm mt-2 space-y-1">
          <li><strong>Strategy Call cancellation:</strong> 48 hours notice for full refund</li>
          <li><strong>Audit cancellation:</strong> Before work starts for refund (minus 10% fee)</li>
          <li><strong>Retainer cancellation:</strong> 30 days notice, no partial month refunds</li>
          <li><strong>First month guarantee:</strong> Retainer clients only, first-time clients</li>
          <li><strong>All refunds:</strong> Email billing@adkarrot.com</li>
        </ul>
      </div>
    </div>
  )
}
